import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, ChevronUp, ExternalLink } from "lucide-react";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavMode, useSidebar } from "@/contexts";
import { useRole } from "@/hooks/auth";
import { useGenieAccess } from "@/hooks/billing/useAccessLimits";
import { Roles } from "@/types/auth/auth";
import {
  adminTabConfig,
  AdminTabKey,
  genieHubNavItems,
  mainNavItems,
  NavItem,
} from "./navConfig";

interface SidebarNavProps {
  sidebarOpen: boolean;
}

// Split a stored nav path like "/learn?tab=playbooks" into the explicit
// { pathname, search } object that React Router's Link expects. Passing the
// raw string with a query suffix to `to` works for the URL push, but consuming
// components that read query state via useSearchParams sometimes fail to
// re-derive on path-only navigations between siblings of the same parent. The
// object form forces React Router to treat search as a first-class change.
const splitNavPath = (raw: string): { pathname: string; search: string } => {
  const qIdx = raw.indexOf("?");
  if (qIdx === -1) return { pathname: raw, search: "" };
  return { pathname: raw.slice(0, qIdx), search: raw.slice(qIdx) };
};

const NavLink = ({
  item,
  sidebarOpen,
  preserveTab,
  onNavigate,
}: {
  item: NavItem;
  sidebarOpen: boolean;
  preserveTab?: string;
  onNavigate?: () => void;
}) => {
  const location = useLocation();
  const { hasRole } = useRole();
  const { canCreateGenie } = useGenieAccess();
  const { pathname: itemPathname, search: itemSearch } = splitNavPath(item.path);
  const isActive = location.pathname.startsWith(itemPathname) && (itemSearch === "" || location.search === itemSearch);

  const isDisabledForUser =
    item.disabled ||
    (item.path === "/user-hub/genie-setup" && !hasRole(Roles.Admin) && !canCreateGenie) ||
    (item.isAdmin && !hasRole([Roles.Admin]));
  const linkPath = preserveTab && item.isAdmin ? `${item.path}?tab=${preserveTab}` : item.path;

  const linkClasses = `
    flex items-center px-2 py-1 rounded transition-all duration-200 justify-between
    ${
      isDisabledForUser
        ? "text-genie-textSecondary/60 cursor-not-allowed"
        : isActive
          ? "text-genie-primary bg-[#E5EAF0] font-medium"
          : "text-genie-textSecondary hover:bg-[#F9FAFC] hover:text-genie-primary"
    }
  `;

  const content = (
    <div className={linkClasses}>
      <div className="flex items-center gap-2">
        {sidebarOpen && <span>{item.title}</span>}
        {sidebarOpen && item.externalUrl && <ExternalLink className="h-3 w-3 text-genie-textSecondary" />}
        {sidebarOpen && item.badge && (
          <Badge className="text-xs bg-genie-primary/10 text-genie-primary px-1.5 py-0.5 h-fit">{item.badge}</Badge>
        )}
      </div>
      {sidebarOpen && item.comingSoon && (
        <span className="text-xs bg-genie-primary/10 text-genie-primary px-1.5 py-0.5 rounded-full font-medium">
          Coming Soon
        </span>
      )}
    </div>
  );

  if (isDisabledForUser) {
    return content;
  }

  if (item.externalUrl) {
    return (
      <a href={item.externalUrl} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return (
    <Link to={preserveTab && item.isAdmin ? linkPath : { pathname: itemPathname, search: itemSearch }} onClick={onNavigate}>
      {content}
    </Link>
  );
};

const NavSection = ({
  section,
  sidebarOpen,
  isExpanded,
  setIsExpanded,
  preserveTab,
  onNavigate,
}: {
  section: NavItem;
  sidebarOpen: boolean;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  preserveTab?: string;
  onNavigate?: () => void;
}) => {
  const location = useLocation();
  const hasChildren = !!section.children && section.children.length > 0;
  const directLinkActive = !hasChildren && location.pathname.startsWith(section.path);
  const firstChild = section.children?.[0];
  const collapsedDestination = firstChild ? splitNavPath(firstChild.path) : null;

  const headerClasses = `
    flex items-center justify-between rounded-lg font-medium select-none cursor-pointer transition-all duration-200 relative
    ${directLinkActive ? "bg-[#E5EAF0] text-genie-primary" : "text-genie-primary hover:bg-[#F9FAFC]"}
    ${sidebarOpen ? "px-2 py-1.5" : "px-3 py-2 justify-center"}
  `;

  const headerInner = (
    <div className="flex items-center gap-2">
      {section.genieIcon ? (
        <GenieIcon name={section.genieIcon} size={20} />
      ) : section.icon ? (
        <section.icon className="h-4 w-4 flex-shrink-0" />
      ) : null}
      {sidebarOpen && <span className="flex-1 font-bold text-genie-logo">{section.title}</span>}
      {sidebarOpen && section.badge && (
        <Badge className="text-[10px] bg-genie-primary/10 text-genie-primary hover:bg-genie-primary/10 px-1.5 py-0 h-4">
          {section.badge}
        </Badge>
      )}
    </div>
  );

  return (
    <li className={sidebarOpen ? "mt-0.5" : "mt-1"}>
      {section.isHeader && (section.icon || section.genieIcon) && (
        <div>
          {hasChildren ? (
            sidebarOpen ? (
              <div className={headerClasses} onClick={() => setIsExpanded(!isExpanded)}>
                {headerInner}
                <div className="ml-1">
                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </div>
              </div>
            ) : collapsedDestination ? (
              <Link to={collapsedDestination} className={headerClasses} onClick={onNavigate}>
                {headerInner}
              </Link>
            ) : (
              <div className={headerClasses}>{headerInner}</div>
            )
          ) : (
            <Link to={section.path} className={headerClasses} onClick={onNavigate}>
              {headerInner}
            </Link>
          )}
        </div>
      )}
      {hasChildren && isExpanded && sidebarOpen && (
        <ul className="ml-5 space-y-0.5 mt-0.5">
          {section.children!.map((child) => (
            <li key={child.path}>
              <NavLink
                item={child}
                sidebarOpen={sidebarOpen}
                preserveTab={preserveTab}
                onNavigate={onNavigate}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const SidebarNav = ({ sidebarOpen }: SidebarNavProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { hasRole } = useRole();
  const { mode, setMode } = useNavMode();
  const { setSidebarOpen } = useSidebar();
  const isActive = (path: string) => location.pathname.startsWith(path);

  // Collapse the sidebar to its icon rail (desktop) / close the drawer (mobile)
  // whenever the user navigates via a nav item, so the chosen page gets full width.
  const handleNavigate = () => setSidebarOpen(false);

  // Get active admin tab from URL
  const activeAdminTab = (searchParams.get("tab") as AdminTabKey) || "users";

  // Force business mode if user is not an admin
  const isAdmin = hasRole([Roles.Admin]);
  const effectiveMode = isAdmin ? mode : "business";

  // Reset mode to business if non-admin somehow has admin mode
  useEffect(() => {
    if (!isAdmin && mode === "admin") {
      setMode("business");
    }
  }, [isAdmin, mode, setMode]);

  // Get the nav items for the current admin tab
  const adminSections = Object.values(adminTabConfig).map((tab) => ({
    tabKey: tab.key,
    title: tab.label,
    path: `/admin?tab=${tab.key}`,
    genieIcon: tab.genieIcon,
    description: tab.description,
    isHeader: true,
    children: tab.items,
  }));

  // Track expansion state for each section (business mode only)
  const [sectionExpansions, setSectionExpansions] = useState<Record<string, boolean>>({});

  // Determine which section contains the active route. Compare against the
  // pathname portion of each child path so entries like "/learn?tab=playbooks"
  // still match when the current URL is "/learn?tab=start".
  const getActiveSectionPath = () => {
    for (const section of genieHubNavItems) {
      if (
        section.children?.some((child) => {
          const childPathname = child.path.split("?")[0];
          return location.pathname.startsWith(childPathname);
        })
      ) {
        return section.path;
      }
    }
    return null;
  };

  const activeSectionPath = getActiveSectionPath();

  const getSectionExpansion = (sectionPath: string) => {
    if (sectionExpansions[sectionPath] === undefined) {
      // Default: expand Genie Operations always, plus the section containing the active route
      return sectionPath === "/genie-hub" || sectionPath === activeSectionPath;
    }
    return sectionExpansions[sectionPath];
  };

  const setSectionExpansion = (sectionPath: string, expanded: boolean) => {
    if (expanded) {
      // Accordion: collapse all others when expanding one
      const newExpansions: Record<string, boolean> = {};
      genieHubNavItems.forEach((section) => {
        newExpansions[section.path] = section.path === sectionPath;
      });
      setSectionExpansions(newExpansions);
    } else {
      setSectionExpansions((prev) => ({ ...prev, [sectionPath]: false }));
    }
  };

  const toggleAllSections = () => {
    const allExpanded = genieHubNavItems.every((section) => getSectionExpansion(section.path));
    const newExpansions: Record<string, boolean> = {};
    genieHubNavItems.forEach((section) => {
      newExpansions[section.path] = !allExpanded;
    });
    setSectionExpansions(newExpansions);
  };

  const allExpanded = genieHubNavItems.every((section) => getSectionExpansion(section.path));
  const getAdminSectionExpansion = (tabKey: AdminTabKey) => sectionExpansions[`admin:${tabKey}`] ?? activeAdminTab === tabKey;
  const setAdminSectionExpansion = (tabKey: AdminTabKey, expanded: boolean) => {
    setSectionExpansions((prev) => ({
      ...prev,
      [`admin:${tabKey}`]: expanded,
    }));
  };

  // Reset expansions when switching to business mode so active-route logic takes over
  useEffect(() => {
    if (effectiveMode === "business") {
      setSectionExpansions({});
    }
  }, [effectiveMode]);

  // Admin mode: VS Code style layout with vertical icon tabs
  if (effectiveMode === "admin") {
    return (
      <nav className="flex-1 flex">
        <ul className={`w-full px-2 ${sidebarOpen ? "space-y-0.5" : "space-y-1"}`}>
          {adminSections.map((section) => {
            return (
            <NavSection
                key={section.path}
                section={section}
                sidebarOpen={sidebarOpen}
                isExpanded={getAdminSectionExpansion(section.tabKey)}
                setIsExpanded={(expanded) => setAdminSectionExpansion(section.tabKey, expanded)}
                preserveTab={section.tabKey}
                onNavigate={handleNavigate}
              />
            );
          })}
        </ul>
      </nav>
    );
  }

  // Collapsed rail: keep it bare for full immersion — no icons, no chrome.
  // Everything returns when the sidebar is expanded.
  if (!sidebarOpen) {
    return null;
  }

  // Business mode: standard sidebar nav
  return (
    <nav className="mt-4 flex flex-col">
      <ul className={`px-2 ${sidebarOpen ? "space-y-0.5" : "space-y-1"}`}>
        {mainNavItems.map((item) => (
          <li key={item.path}>
            <div className="flex items-center">
              <Link
                to={item.path}
                onClick={handleNavigate}
                className={`flex items-center rounded-lg transition-all duration-200 px-2 py-1.5 ${
                  isActive(item.path)
                    ? "text-genie-primary bg-genie-logo/10 font-medium"
                    : "text-genie-primary hover:bg-[#F9FAFC] hover:text-genie-primary font-medium"
                } ${item.path === "/dashboard" ? "flex-1" : "w-full"}`}
              >
                {item.genieIcon ? (
                  <GenieIcon name={item.genieIcon} size={20} />
                ) : item.icon ? (
                  <item.icon className="h-4 w-4 flex-shrink-0 text-genie-primary" />
                ) : null}
                <span className="ml-2 font-bold text-genie-logo">{item.title}</span>
              </Link>
              {item.path === "/dashboard" && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleAllSections}
                        className="h-6 w-6 p-0 ml-2 hover:bg-genie-primary/10 hover:text-genie-primary"
                      >
                        {allExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{allExpanded ? "Collapse all" : "Expand all"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </li>
        ))}
        {genieHubNavItems.map((section) => (
          <NavSection
            key={section.path}
            section={section}
            sidebarOpen={sidebarOpen}
            isExpanded={getSectionExpansion(section.path)}
            setIsExpanded={(expanded) => setSectionExpansion(section.path, expanded)}
            onNavigate={handleNavigate}
          />
        ))}
      </ul>
    </nav>
  );
};

export default SidebarNav;
